import React, { useState } from 'react';
import { toast } from 'react-toastify';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import './ChangePasswordModal.css';

export default function ChangePasswordModal({ forced = false, onClose }) {
  const { user, logout } = useAuth();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirm) return toast.error('Passwords do not match');
    if (form.newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    if (form.newPassword === form.currentPassword) return toast.error('New password must differ from current');
    setLoading(true);
    try {
      await API.post('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      toast.success('Password changed successfully');
      if (forced) {
        // Refresh user context — mustChangePassword is now false
        window.location.reload();
      } else {
        onClose?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const strength = (pwd) => {
    if (!pwd) return 0;
    let s = 0;
    if (pwd.length >= 8) s++;
    if (/[A-Z]/.test(pwd)) s++;
    if (/[0-9]/.test(pwd)) s++;
    if (/[^A-Za-z0-9]/.test(pwd)) s++;
    return s;
  };

  const s = strength(form.newPassword);
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColor = ['', '#ef4444', '#f59e0b', '#3b82f6', '#10b981'];

  return (
    <div className="cpw-overlay">
      <div className="cpw-modal" onClick={e => e.stopPropagation()}>
        <div className="cpw-header">
          <div className="cpw-icon">{forced ? '🔒' : '🔑'}</div>
          <div>
            <h2 className="cpw-title">{forced ? 'Set Your Password' : 'Change Password'}</h2>
            {forced && <p className="cpw-subtitle">Welcome {user?.name}! Please set a new password to continue.</p>}
          </div>
        </div>

        <form onSubmit={submit} className="cpw-form">
          <div className="cpw-field">
            <label>Current Password</label>
            <div className="cpw-input-wrap">
              <input
                type={showCurrent ? 'text' : 'password'}
                name="currentPassword"
                value={form.currentPassword}
                onChange={handle}
                placeholder="Enter current password"
                required
              />
              <button type="button" className="cpw-eye" onClick={() => setShowCurrent(v => !v)}>
                {showCurrent ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div className="cpw-field">
            <label>New Password</label>
            <div className="cpw-input-wrap">
              <input
                type={showNew ? 'text' : 'password'}
                name="newPassword"
                value={form.newPassword}
                onChange={handle}
                placeholder="Min. 6 characters"
                required
              />
              <button type="button" className="cpw-eye" onClick={() => setShowNew(v => !v)}>
                {showNew ? '🙈' : '👁️'}
              </button>
            </div>
            {form.newPassword && (
              <div className="cpw-strength">
                <div className="cpw-strength-bar">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="cpw-strength-seg" style={{ background: i <= s ? strengthColor[s] : '#e2e8f0' }} />
                  ))}
                </div>
                <span style={{ color: strengthColor[s] }}>{strengthLabel[s]}</span>
              </div>
            )}
          </div>

          <div className="cpw-field">
            <label>Confirm New Password</label>
            <div className="cpw-input-wrap">
              <input
                type="password"
                name="confirm"
                value={form.confirm}
                onChange={handle}
                placeholder="Re-enter new password"
                required
              />
              {form.confirm && (
                <span className="cpw-match-icon">
                  {form.confirm === form.newPassword ? '✅' : '❌'}
                </span>
              )}
            </div>
          </div>

          <div className="cpw-actions">
            {!forced && <button type="button" className="cpw-btn-cancel" onClick={onClose}>Cancel</button>}
            <button type="submit" className="cpw-btn-submit" disabled={loading}>
              {loading ? 'Saving…' : forced ? 'Set Password & Continue' : 'Change Password'}
            </button>
          </div>
        </form>

        {forced && (
          <div className="cpw-logout-row">
            <button className="cpw-logout-link" onClick={() => { logout(); window.location.href = '/login'; }}>
              Logout instead
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
